<template>
    <div class="row">
      <div class="col-md-6 col-lg-7 col-xl-8">
        <card class="card mt-3">
          <div class="card-body">
            <div class="form-group">
              <label class="control-label" for="scope_input">Scope</label>
              <select name="scope_input" id="scope_input" class="form-control" v-model="selectedScope" @change="getConfig">
                <option :value="null">Select Scope</option>
                <option v-for="(scope, index) in scopes" :key="index" :value="scope">{{ scope }}</option>
              </select>
            </div>
            <div class="mt-2">
              <div v-if="loading">Please wait...</div>
              <div v-if="!loading && categories && calculations && Object.keys(categories).length > 0">
                <div class="form-group"><input type="text" class="form-control" placeholder="type to filter by term and hit ENTER" v-model.lazy="filter"/></div>
                <ul class="nav nav-tabs mb-3">
                  <li v-for="(tabTitle, index) in categoryTabs" :key="index" class="nav-item">
                    <a :class="{'nav-link':true, 'active': selectedTab==index}" href="javascript:void(0);" @click="selectedTab=index">{{ tabTitle }}</a>
                  </li>
                </ul>
                <ul class="no-bullets" v-for="(tabTitle, index) in categoryTabs" :key="index" v-show="selectedTab==index">
                  <ghg-tree-item
                    class="tree-item"
                    :item="categories[index]"
                    :root-item-index="parseInt(index)"
                    :calculations="calculations"
                    :filter="filter"
                    @ghgInput="handleGhgInput"
                  ></ghg-tree-item>
                </ul>
              </div>
            </div>
          </div>
        </card>
      </div>
      <div class="col-md-6 col-lg-5 col-xl-4">
        <card class="card mt-3 sticky">
          <div class="card-body">
            <div class="text-center">
              <button :class="{btn:true, 'btn-success':true, 'cursor-forbidden': !actions.canEmissionsCalculator}" @click="calculate" :disabled="!actions.canEmissionsCalculator || calculating || !selectedScope">Calculate<span v-if="calculating">...</span></button>
            </div>
            <div class="text-center mt-2" v-if="calculating || (selectedScope && result !== null)">
              <div v-if="calculating">Calculating...</div>
              <div v-show="false" v-if="selectedScope && result !== null">
                <div class="mt-4 mb-1"><img :src="'/images/ghg_calculator/' + selectedScope + '/' + selectedScope + '.svg'" height="70px" :alt="selectedScope"></div>
                <div>{{ selectedScope }}: {{ result }} {{ resultUnit }} of CO2e</div>
              </div>
            </div>
            <div class="mt-4 text-center" v-if="calculatedScopes.scope1.result || calculatedScopes.scope2.result || calculatedScopes.scope3.result">
              <div v-show="false" class="mb-2"><u><b>Overall</b></u></div>
              <div class="row">
                <div class="col-4"><div class="mb-1"><img :src="'/images/ghg_calculator/scope1/scope1.svg'" height="70px" alt="scope1"></div><div>Scope1</div><div><b>{{ calculatedScopes.scope1.result }}</b></div><div>{{ calculatedScopes.scope1.resultUnit }} of CO2e</div></div>
                <div class="col-4"><div class="mb-1"><img :src="'/images/ghg_calculator/scope2/scope2.svg'" height="70px" alt="scope2"></div><div>Scope2</div><div><b>{{ calculatedScopes.scope2.result }}</b></div><div>{{ calculatedScopes.scope2.resultUnit }} of CO2e</div></div>
                <div class="col-4"><div class="mb-1"><img :src="'/images/ghg_calculator/scope3/scope3.svg'" height="70px" alt="scope3"></div><div>Scope3</div><div><b>{{ calculatedScopes.scope3.result }}</b></div><div>{{ calculatedScopes.scope3.resultUnit }} of CO2e</div></div>
              </div>
              <div v-if="calculatedScopes.scope1.percentage || calculatedScopes.scope2.percentage || calculatedScopes.scope3.percentage" class="mt-2">
                <pie-chart
                  :height="200" 
                  :chart-data="{
                    datasets: [
                      {
                        backgroundColor: ['#41B883', '#DD1B16', '#00D8FF'],
                        data: [calculatedScopes.scope1.percentage, calculatedScopes.scope2.percentage, calculatedScopes.scope3.percentage]
                      }
                    ],
                    labels: ['Scope1 %', 'Scope2 %', 'Scope3 %']
                  }" 
                  :extra-options="{responsive: true, maintainAspectRatio: true}">
                </pie-chart>
              </div>
              <div v-if="resultCategories" class="table-responsive mt-4">
                <div class="mb-2">
                  <div>{{ selectedScope }} breakdown</div>
                  <div class="text-muted">tons of CO2e</div>
                </div>
                <table class="table">
                  <tr v-for="(subResult, index) in resultCategories" :key="index"><td class="text-left">{{ categoryTabs[index] }}</td><td>{{ subResult.toFixed(4) }}</td></tr>
                </table>
              </div>
            </div>
          </div>
        </card>
      </div>
    </div>
</template>
<script>
import Api from "../api";
import GhgTreeItem from "../components/GhgTreeItem";
import PieChart from "../components/Charts/PieChart";
export default {
  components: {
    GhgTreeItem,
    PieChart
  },
  data() {
    return {
      calculating: false,
      loading: false,
      scopes: ['scope1', 'scope2', 'scope3'],
      selectedScope: null,
      calculations: null,
      categories: null,
      categoryTabs: null,
      selectedTab: null,
      activityData: {},
      result: null,
      resultUnit: null,
      actions: {
        canEmissionsCalculator: this.$root.canUserDo(['emissions_calculator'])
      },
      filter: '',
      calculatedScopes: {
        scope1: {result: 0, resultUnit: 'tons', percentage: 0}, 
        scope2: {result: 0, resultUnit: 'tons', percentage: 0}, 
        scope3: {result: 0, resultUnit: 'tons', percentage: 0}
      },
      resultCategories: null
    };
  },
  mounted() {},
  methods: {
    getConfig () {
      if (!this.selectedScope) {
        return false;
      }
      let self = this;
      self.result = null;
      self.resultUnit = null;
      self.resultCategories = null;
      self.loading = true;
      self.activityData = {};
      Api.send("GET", "/emissions_calculator/" + this.selectedScope, {}, true)
        .then((response) => {
          self.categories = response.categories;
          self.calculations = response.calculations;
          let tmpTabs = {};
          (Object.keys(response.categories)).forEach((key) => {
            tmpTabs[key] = response.categories[key].title;
          });
          self.categoryTabs = tmpTabs;
          self.selectedTab = Object.keys(response.categories)[0];
        })
        .catch((err) => {
          self.categories = null;
          self.calculations = null;
          self.$root.notifyy('danger', err.msg);
        })
        .finally(() => {
          self.loading = false;
        });
    },
    calculate () {
      let self = this;
      self.calculating = true;
      Api.send("POST", "/emissions_calculator/calculate_" + this.selectedScope, {activityData: this.activityData}, true)
      .then((response) => {
        self.result = response.result;
        self.resultUnit = response.unit;
        self.calculatedScopes[this.selectedScope].result = response.result;
        self.calculatedScopes[this.selectedScope].resultUnit = response.unit;
        self.resultCategories = response.root_categories ? response.root_categories : null;
      })
      .catch((err) => {
        self.result = null;
        self.resultUnit = null;
        self.calculatedScopes[this.selectedScope].result = 0;
        self.calculatedScopes[this.selectedScope].resultUnit = 'tons';
        self.resultCategories = null;
        self.$root.notifyy('danger', err.msg);
      })
      .finally(() => {
        self.calculateScopePercentages();
        self.calculating = false;
      });
    },
    handleGhgInput (value, itemIndex, activityDatumId, co2, ch4, n2o, rootItemIndex) {
      if (!value || value === '' || isNaN(value)) {
        delete this.activityData[itemIndex + '_' + activityDatumId];
      } else {
        this.activityData[itemIndex + '_' + activityDatumId] = {
          input: value,
          co2: co2,
          ch4: ch4,
          n2o: n2o,
          rootItemIndex: rootItemIndex
        };
      }
    },
    calculateScopePercentages () {
      let total = this.calculatedScopes.scope1.result + this.calculatedScopes.scope2.result + this.calculatedScopes.scope3.result;
      this.calculatedScopes.scope1.percentage = Math.round((this.calculatedScopes.scope1.result / total * 100) * 100) / 100;
      this.calculatedScopes.scope2.percentage = Math.round((this.calculatedScopes.scope2.result / total * 100) * 100) / 100;
      this.calculatedScopes.scope3.percentage = Math.round((this.calculatedScopes.scope3.result / total * 100) * 100) / 100;
    }
  },
};
</script>
<style scoped>
ul.no-bullets {
  list-style-type: none;
  padding-left: 20px;
  margin: 0;
}
.cursor-forbidden {
  cursor: not-allowed;
}
@media (min-width: 768px) {
  .sticky {
    position: fixed;
    width: 350px;
  }
}
@media (min-width: 992px) {
  .sticky {
    width: 400px;
  }
}
@media (min-width: 1200px) {
  .sticky {
    width: 450px;
  }
}
</style>
